import { useState, useEffect } from 'react'
import {
  Calendar as CalendarIcon,
  CalendarClock,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format } from 'date-fns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { reviewService } from '@/services/api/reviewService'
import { serverReason, isConflict } from '@/lib/serverReason'
import toast from 'react-hot-toast'

interface RescheduleSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  employeeId: string
  managerId: string
  onSuccess?: () => void
  // The server refused the move because the check-in has moved on (completed, held,
  // cancelled) since the list behind the dialog loaded it; the dialog closes
  onRefused?: () => void
  initiatedBy?: 'employee' | 'manager'  // Determines who becomes the meeting organizer
  // Current session data for pre-population
  currentDate?: string | null
  currentNotes?: string | null
}

export default function RescheduleSessionDialog({
  open,
  onOpenChange,
  sessionId,
  employeeId,
  managerId,
  onSuccess,
  onRefused,
  initiatedBy = 'employee',  // Default to employee for backwards compatibility
  currentDate,
  currentNotes
}: RescheduleSessionDialogProps) {
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined)
  const [scheduledTime, setScheduledTime] = useState<string>('')
  const [duration, setDuration] = useState<number>(30)
  const [reason, setReason] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Pre-populate with current session data when dialog opens
  useEffect(() => {
    if (open && currentDate) {
      // API returns UTC time without timezone indicator, append 'Z' to parse as UTC
      const utcDateString = currentDate.endsWith('Z') ? currentDate : currentDate + 'Z'
      const date = new Date(utcDateString)
      setScheduledDate(date)
      // Extract LOCAL time in HH:mm format (getHours/getMinutes return local time)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      setScheduledTime(`${hours}:${minutes}`)
    }
    if (open && currentNotes) {
      setReason(currentNotes)
    }
  }, [open, currentDate, currentNotes])
  
  // Availability checking state
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [availabilityStatus, setAvailabilityStatus] = useState<'available' | 'conflict' | 'unknown'>('unknown')
  const [conflicts, setConflicts] = useState<Array<{ email: string; employee_id?: string; employee_ids?: string[]; busy_items: any[] }>>([])

  // Check availability when date/time changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!scheduledDate || !scheduledTime || !employeeId || !managerId) {
        setAvailabilityStatus('unknown')
        return
      }
      
      try {
        setIsCheckingAvailability(true)
        
        // Combine date and time
        const [hours, minutes] = scheduledTime.split(':').map(Number)
        const dateTime = new Date(scheduledDate)
        dateTime.setHours(hours, minutes, 0, 0)
        
        const result = await reviewService.checkAvailability(
          [employeeId, managerId],
          dateTime,
          duration,
          null
        )
        
        if (result.error) {
          setAvailabilityStatus('unknown')
        } else if (result.available) {
          setAvailabilityStatus('available')
          setConflicts([])
        } else {
          setAvailabilityStatus('conflict')
          setConflicts(result.conflicts || [])
        }
      } catch (error) {
        console.error('Error checking availability:', error)
        setAvailabilityStatus('unknown')
      } finally {
        setIsCheckingAvailability(false)
      }
    }
    
    const timeoutId = setTimeout(checkAvailability, 500)
    return () => clearTimeout(timeoutId)
  }, [scheduledDate, scheduledTime, duration, employeeId, managerId])

  const resetForm = () => {
    setScheduledDate(undefined)
    setScheduledTime('')
    setDuration(30)
    setReason('')
    setAvailabilityStatus('unknown')
    setConflicts([])
  }

  const handleSubmit = async () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select a date and time')
      return
    }

    try {
      setIsSubmitting(true)
      
      // Combine date and time for scheduled date
      const [hours, minutes] = scheduledTime.split(':').map(Number)
      const scheduledDateTime = new Date(scheduledDate)
      scheduledDateTime.setHours(hours, minutes, 0, 0)
      
      await reviewService.rescheduleReviewSession(
        sessionId,
        scheduledDateTime,
        duration,
        reason || undefined,
        initiatedBy  // Pass who initiated the reschedule to determine meeting organizer
      )

      toast.success('Session rescheduled successfully')
      resetForm()
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error rescheduling session:', error)
      toast.error(serverReason(error, 'Failed to reschedule session'))
      if (isConflict(error)) {
        resetForm()
        onOpenChange(false)
        onRefused?.()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen)
      if (!isOpen) {
        resetForm()
      }
    }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 flex-shrink-0">
          <div>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2 text-white">
              <CalendarClock className="h-6 w-6" />
              Reschedule Check-in
            </DialogTitle>
            <DialogDescription className="text-blue-100 text-sm mt-1">
              Select a new date and time for your check-in
            </DialogDescription>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Preferred Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              New Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {scheduledDate ? format(scheduledDate, 'MMMM do, yyyy') : 'Select date...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  initialFocus
                  disabled={(date) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    return date < today
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preferred Time */}
          <div className="space-y-2">
            <Label htmlFor="time" className="text-sm font-medium">
              New Time <span className="text-red-500">*</span>
            </Label>
            <Select value={scheduledTime} onValueChange={setScheduledTime}>
              <SelectTrigger id="time">
                <SelectValue placeholder="Select time..." />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {/* Business hours: 08:00 to 17:00 */}
                {Array.from({ length: 19 }, (_, i) => {
                  const hours = 8 + Math.floor(i / 2)
                  const minutes = i % 2 === 0 ? '00' : '30'
                  const value = `${String(hours).padStart(2, '0')}:${minutes}`
                  const display = `${hours > 12 ? hours - 12 : hours}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`
                  return (
                    <SelectItem key={value} value={value}>
                      {display}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-sm font-medium">
              Duration
            </Label>
            <Select value={String(duration)} onValueChange={(val) => setDuration(Number(val))}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability Status */}
          {scheduledDate && scheduledTime && (
            <div className="space-y-2">
              {isCheckingAvailability && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking availability...
                </div>
              )}
              {!isCheckingAvailability && availabilityStatus === 'available' && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg border border-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  You and your people leader appear to be available
                </div>
              )}
              {!isCheckingAvailability && availabilityStatus === 'conflict' && conflicts.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800 font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Potential scheduling conflicts detected
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Your people leader may have other commitments at this time.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reason for Rescheduling */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Reason for Rescheduling (Optional)
            </Label>
            <Textarea
              id="reason"
              placeholder="Why do you need to reschedule? (e.g., 'Schedule conflict', 'Need more time to prepare')..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex justify-end gap-3 flex-shrink-0 bg-white">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
              resetForm()
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!scheduledDate || !scheduledTime || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <CalendarClock className="h-4 w-4 mr-2" />
                Reschedule Session
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
